/**
 * UTM Capture Utility Module
 *
 * PURPOSE:
 * Carries campaign attribution (VDAB, Indeed, newsletters, paid social) from the
 * landing URL to the Formie application form, so `campaignSource` reaches Cockpit.
 *
 * Job detail pages are edge-cached and `utm_*` is stripped from the cache key, so
 * a request for `/vacatures/x?utm_source=vdab` never reaches PHP. Capture therefore
 * has to happen in the browser.
 *
 * TWO MECHANISMS:
 * 1. Stateless pass-through (always, no consent needed) - decorates the apply links
 *    on the current page with the campaign values already in `location.search`.
 *    Nothing is stored on the device, so ePrivacy Art. 5(3) does not apply.
 * 2. Cookie write (only with Cookiebot `statistics` consent) - persists the tuple
 *    for 30 days so "landed on job A, applied on job B" stays attributed.
 *
 * `utm_content` is deliberately never touched: that field carries the GA4
 * `transactionId` and is populated by Twig.
 *
 * USAGE:
 * import '~/js/utils/utm-capture'
 */

import { hasConsent } from '~/js/utils/datalayer';

/**
 * Campaign parameter -> first-party cookie name.
 */
export const UTM_COOKIES = {
    utm_source: 'attr_utm_source',
    utm_medium: 'attr_utm_medium',
    utm_campaign: 'attr_utm_campaign',
    utm_term: 'attr_utm_term',
} as const;

export type UtmKey = keyof typeof UTM_COOKIES;

export type UtmValues = Partial<Record<UtmKey, string>>;

/**
 * The four keys we handle, in a stable order.
 */
export const UTM_KEYS = Object.keys(UTM_COOKIES) as UtmKey[];

/**
 * Campaign parameter -> Formie hidden field handle.
 */
export const HIDDEN_FIELD_HANDLES: Record<UtmKey, string> = {
    utm_source: 'utmSource',
    utm_medium: 'utmMedium',
    utm_campaign: 'utmCampaign',
    utm_term: 'utmTerm',
};

/**
 * Persistence window for the cookies, in days (last non-direct touch).
 */
export const TTL_DAYS = 30;

/**
 * Whether the cookie write needs Cookiebot `statistics` consent.
 * Flipping this to false makes the cookies unconditional; it is the single
 * switch to reclassify them as strictly necessary should legal ever allow it.
 */
export const REQUIRE_CONSENT = true;

/**
 * Path suffix identifying an apply page link.
 */
export const APPLY_PATH_SUFFIX = '/solliciteer-online';

/**
 * Defensive cap on a single campaign value. Cockpit's constraints on
 * `campaignSource` are unknown, so values are truncated rather than rejected.
 */
export const MAX_VALUE_LENGTH = 128;

/**
 * Normalise a raw campaign value: trim, drop control characters, cap the length.
 *
 * Control characters are filtered by code point rather than by regular
 * expression so the module stays clean under eslint's `no-control-regex`.
 *
 * @param value - The raw value, typically straight from the query string
 * @returns The sanitised value, possibly an empty string
 */
export function sanitiseValue(value: string | null | undefined): string {
    if (!value) {
        return '';
    }

    const printable = Array.from(value)
        .filter((character) => {
            const code = character.charCodeAt(0);
            return code > 0x1f && code !== 0x7f;
        })
        .join('');

    return printable.trim().slice(0, MAX_VALUE_LENGTH);
}

/**
 * Read the campaign tuple from a query string.
 *
 * Returns an empty object when `utm_source` is missing or empty: without a
 * source there is no campaign to attribute, and a medium on its own would only
 * pollute the stored tuple.
 *
 * @param search - A query string, defaults to the current `location.search`
 * @returns The present, sanitised campaign values
 */
export function readUtmFromUrl(search: string = window.location.search): UtmValues {
    const params = new URLSearchParams(search);
    const values: UtmValues = {};

    UTM_KEYS.forEach((key) => {
        const value = sanitiseValue(params.get(key));

        if (value !== '') {
            values[key] = value;
        }
    });

    if (!values.utm_source) {
        return {};
    }

    return values;
}

/**
 * Read a cookie value by name.
 *
 * Written by hand rather than through the `window.Cookie` global from
 * `head-js.twig`: the load order of that inline script relative to this module
 * bundle is not guaranteed.
 *
 * @param name - The cookie name
 * @returns The decoded value, or an empty string when absent
 */
export function readCookie(name: string): string {
    const prefix = `${encodeURIComponent(name)}=`;

    const match = document.cookie
        .split(';')
        .map((part) => part.trim())
        .find((part) => part.startsWith(prefix));

    if (!match) {
        return '';
    }

    try {
        return decodeURIComponent(match.slice(prefix.length));
    } catch {
        return '';
    }
}

/**
 * Build the shared cookie attribute suffix.
 *
 * `SameSite=Lax` is enough: the cookies only need to survive the top-level GET
 * from the job board and the same-site form POST.
 *
 * @param maxAgeSeconds - `max-age` in seconds, 0 deletes the cookie
 * @returns The attribute string, leading semicolon included
 */
function cookieAttributes(maxAgeSeconds: number): string {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';

    return `; path=/; max-age=${maxAgeSeconds}; SameSite=Lax${secure}`;
}

/**
 * Write a first-party cookie with the module's standard attributes.
 *
 * @param name - The cookie name
 * @param value - The value, URL-encoded before writing
 * @returns void
 */
export function writeCookie(name: string, value: string): void {
    const maxAge = TTL_DAYS * 24 * 60 * 60;

    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}${cookieAttributes(maxAge)}`;
}

/**
 * Delete a first-party cookie previously written by this module.
 *
 * @param name - The cookie name
 * @returns void
 */
export function deleteCookie(name: string): void {
    document.cookie = `${encodeURIComponent(name)}=${cookieAttributes(0)}`;
}

/**
 * Write the campaign tuple atomically.
 *
 * Every key absent from `values` is deleted rather than left alone, so a new
 * campaign never inherits the previous campaign's medium, campaign name or term.
 * Only called when `values.utm_source` is set.
 *
 * @param values - The campaign values to persist
 * @returns void
 */
export function writeTuple(values: UtmValues): void {
    if (!values.utm_source) {
        return;
    }

    UTM_KEYS.forEach((key) => {
        const value = values[key];

        if (value) {
            writeCookie(UTM_COOKIES[key], value);
            return;
        }

        deleteCookie(UTM_COOKIES[key]);
    });
}

/**
 * Read the persisted campaign tuple back out of the cookies.
 *
 * @returns The stored campaign values, empty keys omitted
 */
export function readTupleFromCookies(): UtmValues {
    const values: UtmValues = {};

    UTM_KEYS.forEach((key) => {
        const value = sanitiseValue(readCookie(UTM_COOKIES[key]));

        if (value !== '') {
            values[key] = value;
        }
    });

    return values;
}

/**
 * Persist the tuple when the consent gate allows it.
 *
 * @param values - The campaign values read from the current URL
 * @returns True when the tuple was written
 */
export function persistTuple(values: UtmValues): boolean {
    if (!values.utm_source) {
        return false;
    }

    if (REQUIRE_CONSENT && !hasConsent('statistics')) {
        return false;
    }

    writeTuple(values);

    return true;
}

/**
 * Decide whether an anchor points at the apply page on this origin.
 *
 * @param url - The resolved anchor URL
 * @returns True when the anchor is a same-origin apply link
 */
function isApplyLink(url: URL): boolean {
    if (url.origin !== window.location.origin) {
        return false;
    }

    const path = url.pathname.replace(/\/+$/, '');

    return path.endsWith(APPLY_PATH_SUFFIX);
}

/**
 * Append the campaign values to every apply link in a list of anchors.
 *
 * Stores nothing, so it runs regardless of consent. Existing parameters are
 * never overwritten, which keeps the operation idempotent and lets a hand-built
 * link win over the ambient campaign.
 *
 * @param anchors - The anchors to consider
 * @param values - The campaign values to append
 * @returns The number of anchors that were rewritten
 */
export function decorateAnchors(anchors: ArrayLike<HTMLAnchorElement>, values: UtmValues): number {
    if (!values.utm_source) {
        return 0;
    }

    let decorated = 0;

    Array.from(anchors).forEach((anchor) => {
        let url: URL;

        try {
            url = new URL(anchor.href, window.location.href);
        } catch {
            return;
        }

        if (!isApplyLink(url)) {
            return;
        }

        let changed = false;

        UTM_KEYS.forEach((key) => {
            const value = values[key];

            if (!value || url.searchParams.has(key)) {
                return;
            }

            url.searchParams.set(key, value);
            changed = true;
        });

        if (!changed) {
            return;
        }

        anchor.setAttribute('href', url.toString());
        decorated += 1;
    });

    return decorated;
}

/**
 * Decorate every apply link currently in the document.
 *
 * The apply CTAs (`_organisms/heroes/hero--job.twig`, `_views/view--detail-job.twig`
 * and `_molecules/ctas/cta--job.twig`) are all server-rendered Twig, so a single
 * pass once the DOM is ready is enough; none of them are injected by Alpine or Vue.
 *
 * @param values - The campaign values to append
 * @returns void
 */
export function decorateApplyLinks(values: UtmValues): void {
    decorateAnchors(document.querySelectorAll<HTMLAnchorElement>('a[href]'), values);
}

/**
 * Fill the Formie hidden inputs that are still empty from the cookies.
 *
 * Leg 3 of the handoff: legs 1 (query parameter default) and 2 (the server-side
 * cookie merge in the apply view) already ran, so anything still empty here had
 * no server-side source. `fields[utmContent]` is never touched.
 *
 * @param values - The values to use, defaults to the persisted tuple
 * @returns void
 */
export function fillHiddenInputs(values: UtmValues = readTupleFromCookies()): void {
    UTM_KEYS.forEach((key) => {
        const value = values[key];

        if (!value) {
            return;
        }

        const selector = `input[name="fields[${HIDDEN_FIELD_HANDLES[key]}]"]`;

        document.querySelectorAll<HTMLInputElement>(selector).forEach((input) => {
            if (input.value !== '') {
                return;
            }

            input.value = value;
        });
    });
}

/**
 * Run a callback once the DOM is parsed.
 *
 * @param callback - The callback to run
 * @returns void
 */
function onReady(callback: () => void): void {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', callback, { once: true });
        return;
    }

    callback();
}

/**
 * Wire capture, persistence and handoff.
 *
 * @returns void
 */
export function initUtmCapture(): void {
    const values = readUtmFromUrl();

    persistTuple(values);

    // One source per page, never blended: a URL carrying `utm_source` is the
    // campaign, so the inputs are filled from it alone; only a URL without one
    // falls back to the persisted tuple. Mirrors the server-side rule in
    // `_macros/attribution.twig`.
    const fillSource = (): UtmValues => (values.utm_source ? values : readTupleFromCookies());

    onReady(() => {
        decorateApplyLinks(values);
        fillHiddenInputs(fillSource());
    });

    // Formie re-renders its fields on init (and after an ajax submit), so fill again.
    document.addEventListener('onFormieInit', () => {
        fillHiddenInputs(fillSource());
    });

    // A visitor who accepts after landing still gets the tuple from the current URL.
    window.addEventListener('CookiebotOnAccept', () => {
        const fresh = readUtmFromUrl();

        if (persistTuple(fresh)) {
            fillHiddenInputs(fresh);
        }
    });
}

initUtmCapture();
